import React, { useContext, useEffect, useState } from 'react';
import cn from 'classnames';
import { useField, useFormikContext } from 'formik';
import CloseButton from 'components/UI/CloseButton';
import Loader from 'components/UI/Loader';
import Icon from 'components/UI/Icon';
import { corporateValidation, ErrorsShakeHubContext, ThemeContext } from '../utils';
import { useTooltipState } from 'utils/hooks';
import { FormError } from './../index';

import * as styles from './FormField.module.scss';

export const FormField = ({
  Component,
  className,
  addon,
  inModal,
  isLoading,
  isChecked,
  onClick,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const { isOpen, hideTooltip, showTooltip } = useTooltipState();
  const formik = useFormikContext();
  const [field, meta, helpers] = useField(props.name);
  const isErrorActive = meta.error && meta.touched;
  const errorsShakeHub = useContext(ErrorsShakeHubContext);
  const theme = useContext(ThemeContext);
  const isWithIcon = isLoading || isChecked;

  const validationTooltipText =
    field.name === 'email' && props.validationTooltipText && !corporateValidation(field.value)
      ? props.validationTooltipText
      : undefined;

  const tooltipText = validationTooltipText ?? props?.tooltip?.text ?? undefined;

  const onChange = value => {
    if (typeof props.onChange === 'function') {
      props.onChange(value, formik);
    }

    helpers.setValue(value);
  };

  let preparedProps = {
    formik,
    errorsShakeHub,
    isErrorActive,
    theme,
    focused,
    setFocused,
    inModal,
    ...props,
    onClick,
    onChange,
    onClose: () => {
      helpers.setTouched(true);

      if (typeof props.onClose === 'function') {
        props.onClose();
      }
    },
  };

  if (typeof props.middleware === 'function') {
    preparedProps = props.middleware(preparedProps);
  }

  useEffect(() => {
    if (tooltipText && focused) {
      showTooltip();
    } else {
      hideTooltip();
    }
  }, [tooltipText, focused]);

  useEffect(() => {
    if (typeof props.getInitialValue === 'function' && focused && !meta.touched) {
      preparedProps.onChange(props.getInitialValue());
    }
  }, [focused, formik.dirty]);

  return (
    <div
      className={cn(
        styles.formField,
        preparedProps.withoutError && styles.withoutError,
        isWithIcon && styles.withIcon,
        className,
      )}
      onClick={onClick}
    >
      {tooltipText && isOpen && (
        <div
          className={cn(
            styles.tooltip,
            props?.tooltip?.className && styles[props.tooltip?.className],
          )}
        >
          <CloseButton className={styles.closeTooltip} onClose={hideTooltip} size="sm" />
          <div className={styles.text}>{tooltipText}</div>
        </div>
      )}
      <Component field={field} {...preparedProps} />
      {addon}
      {isLoading && <Loader theme="formField" />}
      {isChecked && <Icon className={styles.checkIcon} name="check" />}
      {!preparedProps.withoutError && <FormError value={meta.error} active={meta.touched} />}
    </div>
  );
};
